import type { FC, PropsWithChildren } from "react";
import { createContext, useCallback, useContext, useMemo, useState } from "react";

export type IModelStackContext = {
    modalStackCounter: number;
};

export const ModelStackContext = createContext<
    IModelStackContext & {
        onOpenModal: () => void;
        onCloseModal: () => void;
    }
>({ onCloseModal: () => null, modalStackCounter: 0, onOpenModal: () => null });

export const useModalStackContext = () => useContext(ModelStackContext);

export const ModelStackContextProvider: FC<PropsWithChildren> = ({ children }) => {
    const [modalStackContext, setModalStackContext] = useState<IModelStackContext>({ modalStackCounter: 0 });

    const onOpenModal = useCallback(() => {
        setModalStackContext((s) => ({ modalStackCounter: s.modalStackCounter + 1 }));
    }, []);

    const onCloseModal = useCallback(() => {
        setModalStackContext((s) => ({ modalStackCounter: s.modalStackCounter - 1 }));
    }, []);

    const context = useMemo(
        () => ({
            modalStackCounter: modalStackContext.modalStackCounter,
            onOpenModal,
            onCloseModal,
        }),
        [modalStackContext.modalStackCounter, onOpenModal, onCloseModal],
    );

    return <ModelStackContext.Provider value={context}>{children}</ModelStackContext.Provider>;
};
